import { useId, useState } from "react"
import { XIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

const KINDS = ["truthy", "isSet", "isEmpty", "==", "!=", ">", ">=", "<", "<="] as const
const NO_VALUE_KINDS: readonly string[] = ["truthy", "isSet", "isEmpty"]

const KIND_LABELS: { [kind: string]: string } = {
  truthy: "Is true",
  isSet: "Has a value",
  isEmpty: "Has no value",
  "==": "Equals",
  "!=": "Does not equal",
  ">": "Greater than",
  ">=": "At least",
  "<": "Less than",
  "<=": "At most",
}

interface Row {
  field: string
  kind: string
  value: string
}

function toRow(condition: PlaybookCondition): Row {
  return { field: condition[0], kind: condition.length === 1 ? "truthy" : String(condition[1]), value: condition.length === 3 ? String(condition[2]) : "" }
}

function toCondition(row: Row): PlaybookCondition | null {
  if (!row.field) return null
  if (row.kind === "truthy") return [row.field]
  if (row.kind === "isSet" || row.kind === "isEmpty") return [row.field, row.kind]
  return [row.field, row.kind as PlaybookOperator, row.value]
}

interface Props {
  conditions: PlaybookCondition[]
  vars: string[]
  onChange: (conditions: PlaybookCondition[]) => void
}

export function ConditionsSection({ conditions, vars, onChange }: Props) {
  const [rows, setRows] = useState<Row[]>(conditions.map(toRow))
  const listId = useId()

  function commit(next: Row[]) {
    setRows(next)
    onChange(next.map(toCondition).filter((c): c is PlaybookCondition => c !== null))
  }

  function updateRow(index: number, patch: Partial<Row>) {
    commit(rows.map((row, i) => i === index ? { ...row, ...patch } : row))
  }

  function addRow() {
    commit([...rows, { field: "", kind: "truthy", value: "" }])
  }

  function removeRow(index: number) {
    commit(rows.filter((_, i) => i !== index))
  }

  return <div className="flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <h3 className="text-sm font-medium">Conditions</h3>
      <span className="text-xs text-muted-foreground">Any one holding is enough (OR)</span>
    </div>
    {rows.map((row, index) => <div key={index} className="flex items-center gap-2">
      <Input value={row.field} onChange={e => updateRow(index, { field: e.target.value })} list={listId} placeholder="var name" className="flex-1" />
      <Select value={row.kind} onValueChange={kind => updateRow(index, { kind })}>
        <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
        <SelectContent>
          {KINDS.map(k => <SelectItem key={k} value={k}>{KIND_LABELS[k]}</SelectItem>)}
        </SelectContent>
      </Select>
      {!NO_VALUE_KINDS.includes(row.kind) && <Input value={row.value} onChange={e => updateRow(index, { value: e.target.value })} placeholder="value" className="w-24" />}
      <Button variant="ghost" size="icon-xs" aria-label="Remove condition" onClick={() => removeRow(index)}><XIcon /></Button>
    </div>)}
    <datalist id={listId}>{vars.map(v => <option key={v} value={v} />)}</datalist>
    <Button variant="outline" size="sm" onClick={addRow} className="self-start">Add condition</Button>
  </div>
}
